import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

interface User {
  id: string;
  username: string;
  role: 'admin' | 'user';
}

interface UserFormData {
  username: string;
  password: string;
  role: 'admin' | 'user';
}

export default function UserManager() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);

  const { register: registerUser, handleSubmit: handleUserSubmit, reset: resetUserForm, formState: { errors: userErrors }, setValue } = useForm<UserFormData>();

  // Consulta per obtenir la llista d'usuaris
  const { data: users, isLoading: isLoadingUsers } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await fetch('/api/auth/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Error carregant usuaris');
      return response.json();
    }
  });

  // Mutació per crear un usuari
  const createUser = useMutation({
    mutationFn: async (userData: UserFormData) => {
      const response = await fetch('/api/auth/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error creant usuari');
      }
    },
    onSuccess: () => {
      setShowUserForm(false);
      resetUserForm();
      setError('');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  // Mutació per actualitzar un usuari
  const updateUser = useMutation({
    mutationFn: async ({ userId, userData }: { userId: string; userData: Partial<UserFormData> }) => {
      const response = await fetch(`/api/auth/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error actualitzant usuari');
      }
    },
    onSuccess: () => {
      setEditingUser(null);
      setShowUserForm(false);
      resetUserForm();
      setError('');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  // Mutació per eliminar un usuari
  const deleteUser = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`/api/auth/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error eliminant usuari');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  const handleEdit = (user: User) => {
    setEditingUser(user.id);
    setShowUserForm(true);
    setValue('username', user.username);
    setValue('role', user.role);
    // No establim password, ja que no volem canviar-la necessàriament
  };

  const handleDelete = (userId: string) => {
    if (window.confirm('Estàs segur que vols eliminar aquest usuari?')) {
      deleteUser.mutate(userId);
    }
  };

  const onUserSubmit = (data: UserFormData) => {
    if (editingUser) {
      // Si estem editant, no enviem la contrasenya si està buida
      const updateData: Partial<UserFormData> = {
        username: data.username,
        role: data.role
      };
      
      if (data.password) {
        updateData.password = data.password;
      }
      
      updateUser.mutate({ userId: editingUser, userData: updateData });
    } else {
      createUser.mutate(data);
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setShowUserForm(false);
    resetUserForm();
  };

  if (isLoadingUsers) {
    return <div className="text-center py-8">Carregant...</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Gestió d'usuaris</h2>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <button
        onClick={() => {
          setEditingUser(null);
          resetUserForm();
          setShowUserForm(!showUserForm);
        }}
        className="mb-4 bg-black text-white px-4 py-2 rounded-md hover:bg-gray-900 transition-colors"
      >
        {showUserForm && !editingUser ? 'Cancel·lar' : 'Crear nou usuari'}
      </button>

      {showUserForm && (
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium mb-4">
            {editingUser ? 'Editar usuari' : 'Crear nou usuari'}
          </h3>
          <form onSubmit={handleUserSubmit(onUserSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nom d'usuari</label>
              <input
                type="text"
                {...registerUser('username', { required: 'Aquest camp és obligatori' })}
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              />
              {userErrors.username && (
                <p className="text-red-500 text-xs mt-1">{userErrors.username.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Contrasenya {editingUser && '(deixa en blanc per mantenir l\'actual)'}
              </label>
              <input
                type="password"
                {...registerUser('password', { 
                  required: editingUser ? false : 'Aquest camp és obligatori', 
                  minLength: {
                    value: 6,
                    message: 'La contrasenya ha de tenir almenys 6 caràcters'
                  }
                })}
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              />
              {userErrors.password && (
                <p className="text-red-500 text-xs mt-1">{userErrors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Rol</label>
              <select
                {...registerUser('role', { required: 'Aquest camp és obligatori' })}
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              >
                <option value="user">Usuari</option>
                <option value="admin">Administrador</option>
              </select>
              {userErrors.role && (
                <p className="text-red-500 text-xs mt-1">{userErrors.role.message}</p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={createUser.isPending || updateUser.isPending}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {editingUser
                  ? updateUser.isPending ? 'Actualitzant...' : 'Actualitzar'
                  : createUser.isPending ? 'Creant...' : 'Crear'}
              </button>
              {editingUser && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
                >
                  Cancel·lar
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Taula d'usuaris */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom d'usuari</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Accions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users?.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{user.username}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {user.role === 'admin' ? 'Administrador' : 'Usuari'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleEdit(user)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Editar"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Eliminar"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}